// ============================================================================
// FSA OPENING BALANCE VERIFICATION TAB  (ADR-0007)
// ============================================================================
// Verifies the CLIENT's current-year opening TB against this engagement's
// consolidated opening balances (the PY closing TB — auto-seeded on
// roll-forward, or the uploaded Opening TB on first-year engagements).
// P&L ledgers are expected nil in the client's opening; the PY profit
// transfer to reserve/capital ledgers is recognised as an explained
// difference. Unclassified PY ledgers with no client match are ignored.
//
// Verification-only working paper — the upload never enters the TB
// consolidation pipeline. Upload runs through a TB-style wizard with column
// mapping (FsaObUploadModal); status/remark per difference is maintained via
// a modal from each row's action menu (FsaObAnnotationModal).
// Layout mirrors the Depreciation worksheet (toolbar + full-height panel).

import React, { useCallback, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ActionMenu, type ActionMenuItem } from 'components/ui';
import notificationService from 'services/notificationService';
import { fsaObVerificationService } from 'api/services/fsa/fsaObVerificationService';
import { useFsaObVerification } from 'hooks/api/fsa/useFsaObVerification';
import FsaObUploadModal from 'components/modals/modules/fsa/FsaObUploadModal';
import FsaObAnnotationModal from 'components/modals/modules/fsa/FsaObAnnotationModal';
import type { ID } from 'api/types';
import type {
  ObAnnotationStatus,
  ObComparisonRow,
} from 'api/types/fsa/obVerification.types';

interface Props {
  engagementId: ID;
  tabId?: string;
  moduleName?: string;
}

type FilterKey = 'all' | 'differences' | 'suggested' | 'missing' | 'new' | 'pl' | 'explained';
type SortKey = 'default' | 'ledger' | 'py' | 'client' | 'delta';

const TOLERANCE = 0.01;

const fmt = (v: number | null | undefined): string =>
  v == null ? '—' : v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtBracket = (v: number | null | undefined): string =>
  v == null ? '—' : v < 0 ? `(${fmt(Math.abs(v))})` : fmt(v);

const sectionLabel = (r: ObComparisonRow): string =>
  r.section === 'RESERVE' ? 'Reserves/Capital'
    : r.section === 'UNCLASSIFIED' ? 'Unclassified'
    : r.section;

const FsaObVerificationTab: React.FC<Props> = ({ engagementId }) => {
  const { workspace, setWorkspace, loading, error, refetch } = useFsaObVerification(engagementId);

  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [annotationRow, setAnnotationRow] = useState<ObComparisonRow | null>(null);
  const [savingAnnotation, setSavingAnnotation] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);
  const [filter, setFilter] = useState<FilterKey>('differences');
  // Column sorting — 'default' = server relevance order (section, then
  // |Δ| desc). Amount columns sort by ABSOLUTE value so identical /
  // near-identical balances land next to each other for eyeball matching.
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'default', dir: 'desc' });

  const comparison = workspace?.comparison ?? null;
  const summary = comparison?.summary ?? null;

  // ---- annotations (saved via the modal) -----------------------------------
  const saveAnnotation = useCallback(async (ledgerKey: string, status: ObAnnotationStatus, remark: string | null) => {
    if (!workspace) return;
    const annotations = { ...workspace.header.annotations };
    if (status === 'UNVERIFIED' && !remark?.trim()) {
      delete annotations[ledgerKey];
    } else {
      annotations[ledgerKey] = { status, remark: remark?.trim() || null };
    }
    setSavingAnnotation(true);
    try {
      const r = await fsaObVerificationService.saveAnnotations(engagementId, {
        annotations,
        rejectedMatches: workspace.header.rejectedMatches ?? [],
        rowVersion: workspace.header.rowVersion,
      });
      if (r.data) setWorkspace(r.data);
      setAnnotationRow(null);
      notificationService.toast.success('Verification note saved.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Error saving the note — reload and retry.');
    } finally {
      setSavingAnnotation(false);
    }
  }, [engagementId, workspace, setWorkspace]);

  /** "Not the same ledger" on a suggested match — persists the rejection and re-splits the pair. */
  const unlinkMatch = useCallback(async (row: ObComparisonRow) => {
    if (!workspace || !row.clientLedgerKey) return;
    const pairId = `${row.ledgerKey}||${row.clientLedgerKey}`;
    setSavingAnnotation(true);
    try {
      const r = await fsaObVerificationService.saveAnnotations(engagementId, {
        annotations: workspace.header.annotations,
        rejectedMatches: [...new Set([...(workspace.header.rejectedMatches ?? []), pairId])],
        rowVersion: workspace.header.rowVersion,
      });
      if (r.data) setWorkspace(r.data);
      notificationService.toast.success('Pair unlinked — the ledgers show separately again.');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'Error unlinking the pair — reload and retry.');
    } finally {
      setSavingAnnotation(false);
    }
  }, [engagementId, workspace, setWorkspace]);

  // ---- exports -------------------------------------------------------------
  const handleExport = useCallback(async (kind: 'pdf' | 'excel') => {
    setExporting(kind);
    try {
      const blob = kind === 'pdf'
        ? await fsaObVerificationService.getPdfBlob(engagementId)
        : await fsaObVerificationService.getExcelBlob(engagementId);
      const url = URL.createObjectURL(blob);
      if (kind === 'pdf') {
        const opened = window.open(url, '_blank');
        if (!opened) notificationService.toast.warning('Pop-up blocked. Allow pop-ups so the PDF can open.', 6000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = `FSA-Opening-Balance-Verification-${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        notificationService.toast.success('Download started.');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? `Error generating ${kind === 'pdf' ? 'PDF' : 'Excel'}.`);
    } finally {
      setExporting(null);
    }
  }, [engagementId]);

  // ---- filtering -----------------------------------------------------------
  const matchesFilter = useCallback((r: ObComparisonRow, f: FilterKey): boolean => {
    switch (f) {
      case 'differences':
        return Math.abs(r.delta) > TOLERANCE && !r.explainedByProfitTransfer;
      case 'suggested': return r.matchType === 'SUGGESTED_MATCH';
      case 'missing':   return r.matchType === 'MISSING_IN_CLIENT' && Math.abs(r.delta) > TOLERANCE;
      case 'new':       return r.matchType === 'NEW_IN_CLIENT';
      case 'pl':        return r.section === 'PL';
      case 'explained': return r.explainedByProfitTransfer || r.annotationStatus === 'EXPLAINED';
      default:          return true;
    }
  }, []);

  const filteredRows = useMemo(() => {
    const rows = (comparison?.rows ?? []).filter(r => matchesFilter(r, filter));
    if (sort.key === 'default') return rows;
    const dirMul = sort.dir === 'asc' ? 1 : -1;
    const keyOf = (r: ObComparisonRow): number | string => {
      switch (sort.key) {
        case 'ledger': return r.ledgerName.toLowerCase();
        // Amount columns sort by ABSOLUTE value — similar balances group together.
        case 'py':     return Math.abs(r.pyAuditedClosing ?? 0);
        case 'client': return Math.abs(r.clientOpening ?? 0);
        default:       return Math.abs(r.delta);
      }
    };
    return [...rows].sort((a, b) => {
      const ka = keyOf(a);
      const kb = keyOf(b);
      const cmp = typeof ka === 'string'
        ? ka.localeCompare(kb as string)
        : (ka as number) - (kb as number);
      return cmp * dirMul;
    });
  }, [comparison, filter, matchesFilter, sort]);

  const toggleSort = useCallback((key: SortKey) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: key === 'ledger' ? 'asc' : 'desc' });
  }, []);

  const filterCounts = useMemo(() => {
    const rows = comparison?.rows ?? [];
    const counts: Record<FilterKey, number> = {
      all: rows.length,
      differences: 0, suggested: 0, missing: 0, new: 0, pl: 0, explained: 0,
    };
    for (const r of rows) {
      (['differences', 'suggested', 'missing', 'new', 'pl', 'explained'] as FilterKey[]).forEach(f => {
        if (matchesFilter(r, f)) counts[f]++;
      });
    }
    return counts;
  }, [comparison, matchesFilter]);

  // ---- render --------------------------------------------------------------
  if (loading && !workspace) return <CenterNote text="Loading opening balance verification…" />;
  if (error && !workspace) return <CenterNote text={error} isError onRetry={refetch} />;
  if (!workspace) return <CenterNote text="Opening balance verification unavailable." />;

  if (!workspace.hasOpeningBasis) {
    return (
      <CenterNote text="This engagement has no opening balances yet. Upload/consolidate the Opening TB (previous year closing TB) in the Trial Balance tab first — that is the basis the client's opening TB is verified against." />
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* toolbar — single row; the basis text truncates instead of wrapping
          so the table keeps its vertical space */}
      <div style={toolbarStyle}>
        <div
          style={{
            fontSize: '0.8125rem', color: 'var(--text-secondary)',
            flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
          title={`Basis: ${workspace.basisDescription ?? '—'}`
            + (workspace.header.originalFileName ? ` · Client TB: ${workspace.header.originalFileName} (${workspace.header.rowCount} ledgers)` : '')}
        >
          Basis: <b>{workspace.basisDescription ?? '—'}</b>
          {workspace.header.originalFileName && (
            <> · Client TB: <b>{workspace.header.originalFileName}</b> ({workspace.header.rowCount} ledgers)</>
          )}
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleExport('pdf')}
          disabled={exporting != null || !comparison} title="Verification report PDF">
          <IconSystem.print size={14} /> {exporting === 'pdf' ? 'Rendering…' : 'Print'}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleExport('excel')}
          disabled={exporting != null || !comparison} title="Verification workbook">
          <IconSystem.download size={14} /> {exporting === 'excel' ? 'Building…' : 'Excel'}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={refetch} disabled={loading} title="Recompute the comparison">
          <IconSystem.refresh size={14} />
        </button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => setUploadModalOpen(true)}>
          <IconSystem.upload size={14} /> {workspace.header.rowCount > 0 ? 'Replace Client TB' : 'Upload Client TB'}
        </button>
      </div>

      {/* full-height panel — matches the Depreciation worksheet sections */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '0.5rem 0.75rem 0.75rem' }}>
        <div style={panelStyle}>
          {/* compact header — title + single-line inline summary. The old
              title/subtitle band + tile-grid summary cost ~200px of table
              space; the working area is the priority. */}
          <div style={panelHeaderStyle}>
            <span style={{ fontWeight: 700, fontSize: '0.8125rem', letterSpacing: 0.2, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>
              Opening Balance Comparison
            </span>
            {summary && (
              <div style={inlineSummaryStyle}>
                <InlineStat label="PY BS" value={fmtBracket(summary.pyBsTotal)} />
                <InlineStat label="P&L → Reserves" value={fmtBracket(summary.pyPlNet)} />
                <InlineStat label="Client total" value={fmtBracket(summary.clientTotal)} />
                <InlineStat
                  label="Transfer"
                  value={summary.profitTransferExplained ? '✓ recognised' : 'not matched'}
                  tone={summary.profitTransferExplained ? 'ok' : 'warn'}
                />
                <InlineStat
                  label="Unexplained"
                  value={fmtBracket(summary.unexplainedDifference)}
                  tone={Math.abs(summary.unexplainedDifference) <= TOLERANCE ? 'ok' : 'warn'}
                  strong
                />
              </div>
            )}
          </div>

          {!comparison ? (
            <CenterNote text="Upload the client's current-year opening trial balance to compare it against the previous year's closing balances (with P&L collapsed into reserves)." />
          ) : (
            <>

              {/* filter pills */}
              <div style={pillBarStyle}>
                {([
                  ['differences', 'Differences'],
                  ['suggested', 'Possible matches'],
                  ['missing', 'Missing in client'],
                  ['new', 'New in client'],
                  ['pl', 'P&L ledgers'],
                  ['explained', 'Explained'],
                  ['all', 'All ledgers'],
                ] as Array<[FilterKey, string]>).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    style={filter === key ? pillActiveStyle : pillStyle}
                    onClick={() => setFilter(key)}
                  >
                    {label}
                    <span style={{
                      marginLeft: 6, padding: '0 6px', borderRadius: 999, fontSize: '0.64rem', fontWeight: 700,
                      background: filter === key ? 'rgba(255,255,255,0.25)' : 'var(--bg-secondary)',
                      color: filter === key ? 'white' : 'var(--text-secondary)',
                    }}>
                      {filterCounts[key]}
                    </span>
                  </button>
                ))}
              </div>

              {/* comparison table */}
              <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 1rem 1rem' }}>
                {filteredRows.length === 0 ? (
                  <div style={{ padding: '1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {filter === 'differences'
                      ? '✓ No unexplained differences — the client\'s opening TB ties out to the previous year\'s closing balances.'
                      : 'No ledgers match this filter.'}
                  </div>
                ) : (
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <SortTh label="Ledger" sortKey="ledger" sort={sort} onToggle={toggleSort} />
                        <Th>Section</Th>
                        <SortTh label="PY Closing (per FSA)" sortKey="py" sort={sort} onToggle={toggleSort} right />
                        <SortTh label="Client Opening" sortKey="client" sort={sort} onToggle={toggleSort} right />
                        <SortTh label="Δ" sortKey="delta" sort={sort} onToggle={toggleSort} right />
                        <Th>Status</Th><Th> </Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRows.map(r => {
                        const hasDiff = Math.abs(r.delta) > TOLERANCE;
                        const actions: ActionMenuItem[] = [{
                          id: 'annotate',
                          label: 'Status / Remark…',
                          icon: <IconSystem.edit size={14} />,
                          onClick: () => setAnnotationRow(r),
                        }];
                        if (r.matchType === 'SUGGESTED_MATCH') {
                          actions.push({
                            id: 'unlink',
                            label: 'Not the same ledger (unlink)',
                            icon: <IconSystem.x size={14} />,
                            onClick: () => unlinkMatch(r),
                            variant: 'danger',
                          });
                        }
                        return (
                          <tr key={r.ledgerKey}>
                            <td style={tdStyle}>
                              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 600 }}>{r.ledgerName}</span>
                                {r.matchType === 'MISSING_IN_CLIENT' && <span style={chipStyle('#fee2e2', '#991b1b')}>missing in client</span>}
                                {r.matchType === 'NEW_IN_CLIENT' && <span style={chipStyle('#dbeafe', '#1e40af')}>new in client</span>}
                                {r.matchType === 'SUGGESTED_MATCH' && (
                                  <span style={chipStyle('#ede9fe', '#5b21b6')} title={r.matchReason ?? undefined}>
                                    possible match
                                  </span>
                                )}
                                {r.explainedByProfitTransfer && <span style={chipStyle('#dcfce7', '#166534')}>profit transfer ✓</span>}
                              </div>
                              {r.matchType === 'SUGGESTED_MATCH' && r.clientLedgerName && (
                                <div style={{ fontSize: '0.7rem', color: '#5b21b6', marginTop: 1 }} title={r.matchReason ?? undefined}>
                                  ↔ client: {r.clientLedgerName}
                                </div>
                              )}
                              {r.parentGroup && (
                                <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 1 }}>
                                  {r.parentGroup}
                                </div>
                              )}
                            </td>
                            <td style={{ ...tdStyle, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{sectionLabel(r)}</td>
                            <td style={numTd}>{fmtBracket(r.pyAuditedClosing)}</td>
                            <td style={numTd}>{fmtBracket(r.clientOpening)}</td>
                            <td style={{
                              ...numTd, fontWeight: 600,
                              color: !hasDiff || r.explainedByProfitTransfer ? '#16a34a' : '#d97706',
                            }}>
                              {fmtBracket(r.delta)}
                            </td>
                            <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                              {r.explainedByProfitTransfer ? (
                                <span style={chipStyle('#dcfce7', '#166534')}>Explained</span>
                              ) : r.annotationStatus === 'EXPLAINED' ? (
                                <span style={chipStyle('#dcfce7', '#166534')}>Explained</span>
                              ) : r.annotationStatus === 'TO_ADJUST' ? (
                                <span style={chipStyle('#fef3c7', '#92400e')}>To adjust</span>
                              ) : hasDiff ? (
                                <span style={chipStyle('#f1f5f9', '#475569')}>Unverified</span>
                              ) : (
                                <span style={{ color: '#16a34a', fontSize: '0.75rem' }}>✓</span>
                              )}
                              {r.annotationRemark && (
                                <div style={{
                                  fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: 2,
                                  maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }} title={r.annotationRemark}>
                                  {r.annotationRemark}
                                </div>
                              )}
                            </td>
                            <td style={{ ...tdStyle, width: 44 }}>
                              <ActionMenu items={actions} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <div style={footNoteStyle}>
                  PY Closing (per FSA) = this engagement's consolidated opening balances — the PY closing TB
                  (auto-seeded from prior audited closings on roll-forward, or the uploaded Opening TB on a
                  first-year engagement). Unclassified PY ledgers with no client match are ignored. This
                  worksheet is verification-only: the uploaded TB never changes the engagement's own opening figures.
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* modals */}
      <FsaObUploadModal
        isOpen={uploadModalOpen}
        engagementId={engagementId}
        onUploaded={(ws) => setWorkspace(ws)}
        onClose={() => setUploadModalOpen(false)}
      />
      <FsaObAnnotationModal
        isOpen={annotationRow != null}
        row={annotationRow}
        saving={savingAnnotation}
        onSave={saveAnnotation}
        onClose={() => setAnnotationRow(null)}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// small components + styles
// ---------------------------------------------------------------------------

/** One inline label:value pair in the compact header summary. */
const InlineStat: React.FC<{ label: string; value: string; tone?: 'ok' | 'warn'; strong?: boolean }> = ({ label, value, tone, strong }) => (
  <span style={{ whiteSpace: 'nowrap' }}>
    <span style={{ fontSize: '0.68rem', color: 'var(--text-secondary)' }}>{label} </span>
    <span style={{
      fontSize: '0.78rem', fontWeight: strong ? 700 : 600, fontVariantNumeric: 'tabular-nums',
      color: tone === 'ok' ? '#16a34a' : tone === 'warn' ? '#d97706' : 'var(--text-primary)',
    }}>
      {value}
    </span>
  </span>
);

const Th: React.FC<{ right?: boolean; children: React.ReactNode }> = ({ right, children }) => (
  <th style={{ ...thStyle, textAlign: right ? 'right' : 'left' }}>{children}</th>
);

/** Clickable sort header — amount columns sort by absolute value so similar balances group together. */
const SortTh: React.FC<{
  label: string;
  sortKey: SortKey;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onToggle: (key: SortKey) => void;
  right?: boolean;
}> = ({ label, sortKey, sort, onToggle, right }) => {
  const active = sort.key === sortKey;
  return (
    <th
      style={{ ...thStyle, textAlign: right ? 'right' : 'left', cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onToggle(sortKey)}
      title={sortKey === 'ledger' ? 'Sort by ledger name' : 'Sort by amount (absolute — similar balances group together)'}
    >
      {label}
      <span style={{ marginLeft: 4, opacity: active ? 1 : 0.3 }}>
        {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );
};

const CenterNote: React.FC<{ text: string; isError?: boolean; onRetry?: () => void }> = ({ text, isError, onRetry }) => (
  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, height: '100%', padding: '2rem', textAlign: 'center', color: isError ? '#dc2626' : 'var(--text-secondary)', fontSize: '0.85rem' }}>
    {text}
    {onRetry && <button type="button" className="btn btn-secondary btn-sm" onClick={onRetry}>Retry</button>}
  </div>
);

const chipStyle = (bg: string, color: string): React.CSSProperties => ({
  display: 'inline-block', marginLeft: 6, padding: '1px 8px', borderRadius: 999,
  background: bg, color, fontSize: '0.64rem', fontWeight: 600, whiteSpace: 'nowrap',
});

const toolbarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  flexWrap: 'nowrap',
};
const panelStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid var(--border-color)',
  borderRadius: 8,
  background: 'var(--bg-primary)',
  overflow: 'hidden',
};
const panelHeaderStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 16,
  padding: '0.45rem 1rem',
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
  flexShrink: 0,
  flexWrap: 'wrap',
};
const inlineSummaryStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'baseline', gap: 14,
  flexWrap: 'wrap',
  marginLeft: 'auto',
};
const pillBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '0.4rem 1rem',
  flexWrap: 'wrap',
  flexShrink: 0,
};
const pillStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center',
  padding: '3px 10px',
  border: '1px solid var(--form-border-color)',
  borderRadius: 999,
  background: 'transparent',
  color: 'var(--text-primary)',
  cursor: 'pointer',
  fontSize: '0.75rem',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};
const pillActiveStyle: React.CSSProperties = {
  ...pillStyle,
  background: 'var(--primary-color, #2563eb)',
  color: 'white',
  borderColor: 'var(--primary-color, #2563eb)',
};
const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem',
};
const thStyle: React.CSSProperties = {
  padding: '8px 10px',
  borderBottom: '2px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.4,
  color: 'var(--text-secondary)', whiteSpace: 'nowrap',
  position: 'sticky', top: 0, zIndex: 1,
};
const tdStyle: React.CSSProperties = {
  padding: '4px 10px',
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
  verticalAlign: 'middle',
};
const numTd: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};
const footNoteStyle: React.CSSProperties = {
  marginTop: 8, fontSize: '0.68rem', color: 'var(--text-secondary)', lineHeight: 1.4,
};

export default FsaObVerificationTab;
